import { useMemo } from 'react';
import type { AllDetailsFiltersState, AllDetailsSortState, DashboardQualitySettings } from '@/entities/dashboards';

/** Префикс идентификатора атрибутной колонки таблицы «Все детали» в настройках грида. */
const ATTRIBUTE_COLUMN_PREFIX = 'attr_';

type Params = {
  /** Текущая сортировка таблицы; `null` — сортирует бэкенд по умолчанию. */
  detailsSort: AllDetailsSortState | null;
  /** Колоночные фильтры таблицы. */
  detailsFilters: AllDetailsFiltersState;
  /** Настройки колонок таблицы из uiSettings. */
  allDetailsColumns: DashboardQualitySettings['allDetailsColumns'];
};

/** Сериализованные параметры запроса таблицы «Все детали». */
export type AllDetailsQueryParams = {
  /** Значение `sort`; `null` — параметр не передаётся. */
  sortParam: string | null;
  /** Значение `filter-block-rating`; `null` — ни по одному блоку границ не задано. */
  blockRatingParam: string | null;
  /** Значение `columns` — JSON-массив запрашиваемых `attribute_path`-ов. */
  columnsParam: string;
};

/**
 * Перевод состояния таблицы «Все детали» (сортировка, колоночные фильтры, состав колонок)
 * в query-параметры её запроса: отбор и порядок строк делает бэкенд, а не клиент.
 */
export const useAllDetailsQueryParams = ({
  detailsSort,
  detailsFilters,
  allDetailsColumns,
}: Params): AllDetailsQueryParams => {
  const sortParam = useMemo(() => {
    if (!detailsSort) return null;
    if (detailsSort.key === 'blockRating') {
      if (typeof detailsSort.blockId !== 'number') return null;
      return `blockRating:${detailsSort.blockId}:${detailsSort.direction}`;
    }
    return `${detailsSort.key}:${detailsSort.direction}`;
  }, [detailsSort]);

  const blockRatingParam = useMemo(() => {
    const entries = Object.entries(detailsFilters.blockRatings ?? {});
    if (entries.length === 0) return null;
    const arr = entries
      .map(([blockIdRaw, range]) => {
        const blockId = Number(blockIdRaw);
        if (!Number.isFinite(blockId)) return null;
        const item: { blockId: number; from?: number; to?: number } = { blockId };
        if (range.from !== undefined) item.from = range.from;
        if (range.to !== undefined) item.to = range.to;
        if (item.from === undefined && item.to === undefined) return null;
        return item;
      })
      .filter((v): v is { blockId: number; from?: number; to?: number } => v !== null);
    return arr.length > 0 ? JSON.stringify(arr) : null;
  }, [detailsFilters.blockRatings]);

  // `visibleColumns` в uiSettings хранит идентификаторы колонок DataGrid; у атрибутных колонок
  // они несут префикс, который здесь и срезается до чистого `attribute_path`. Пересечения с
  // доступными колонками нет: неизвестные коды бэкенд игнорирует молча, а пересечение замкнуло
  // бы данные в круг (доступные колонки ← ответ ← запрошенные колонки) и стоило бы лишнего
  // запроса при первой загрузке.
  //
  // Пустой массив уходит явно, а не пропускается: так бэкенд знает, что атрибуты не
  // запрашиваются. Атрибутные фильтры ведут себя иначе — пустой набор параметра не даёт.
  const columnsParam = useMemo(() => {
    const visibleColumns = allDetailsColumns?.visibleColumns ?? [];
    const codes = visibleColumns
      .filter((id) => id.startsWith(ATTRIBUTE_COLUMN_PREFIX))
      .map((id) => id.slice(ATTRIBUTE_COLUMN_PREFIX.length));
    return JSON.stringify(codes);
  }, [allDetailsColumns?.visibleColumns]);

  return { sortParam, blockRatingParam, columnsParam };
};
